import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FIELD_NAME, ROUTE_PATHS } from '@constants';
import { CAMPAIGN_TYPE_ID } from '@enums';
import { useStore } from '@hooks';
import { Form, Layout } from 'antd';
import { observer } from 'mobx-react';
import Preview from '../../components/Preview';
import { useFormInit } from '../../form';
import { useCalculatePriority } from '../../hooks/useCalculatePriority';
import Step5Content from './components/Content';

const { Sider } = Layout;

const Step5 = observer(() => {
    const navigate = useNavigate();
    const params = useParams();

    const store = useStore();
    const {
        accountProductsIds,
        pending,
        previewProductsPending,
        productsSelectionPending,
        formSubmitting,
        campaign,
        setIsNextDisabled,
        setIsNextLoading,
        updateCampaign,
        submitCampaign,
        syncSelectionWithServer,
        setFormSubmitting,
        syncSelectedCollections,
        syncSelectedProductTypes,
        previewProductsLength,
        userIncludedIds,
        userExcludedIds
    } = store.campaignWizardStore;
    const { getCampaignCommissions } = store.campaignsStore;
    const { updateCommissions, currentAccount } = store.accountsStore;

    const calculatePriority = useCalculatePriority();
    const form = useFormInit();
    Form.useWatch(FIELD_NAME.CAMPAIGN.NAME, form);

    const nextDisabled = !previewProductsLength || !form.getFieldValue(FIELD_NAME.CAMPAIGN.NAME);

    const nextLoading = pending || previewProductsPending || productsSelectionPending || formSubmitting;

    useEffect(() => {
        setIsNextLoading(nextLoading);
    }, [nextLoading]);

    useEffect(() => {
        setIsNextDisabled(nextDisabled);
    }, [nextDisabled]);

    const recalculatePriority = async ({ saveResultInDb = false }) => {
        const { COMMISSION, BOOST } = FIELD_NAME.CAMPAIGN;
        const { REST_OF_SITE_COMMISSION, NEW_CUSTOMER_COMMISSION, COLD_CUSTOMER_COMMISSION } = FIELD_NAME.ACCOUNT;
        const priorityValues = form.getFieldsValue([
            COMMISSION,
            BOOST,
            REST_OF_SITE_COMMISSION,
            NEW_CUSTOMER_COMMISSION,
            COLD_CUSTOMER_COMMISSION
        ]);
        await calculatePriority(priorityValues, saveResultInDb);
    };

    const handleFinish = async ({
        boostId,
        title,
        campaignCommission,
        coldCustomerCommission,
        newCustomerCommission,
        restOfSiteCommission
    }) => {
        try {
            setFormSubmitting(true);

            // Capture user-edited inclusions/exclusions before syncSelectionWithServer clears them.
            // Used by submitCampaign to recover exclusions when BE rebuilds an empty campaign on submit.
            const submitSelections = {
                userIncludedIds: [...userIncludedIds],
                userExcludedIds: [...userExcludedIds]
            };

            // update selected collections and campaign products
            if (campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_COLLECTIONS) {
                await syncSelectedCollections();
            }

            // update selected product types and campaign products
            if (campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCT_TYPES) {
                await syncSelectedProductTypes();
            }

            await syncSelectionWithServer();

            const commissions = [
                { form: coldCustomerCommission, store: currentAccount.commission.coldCustomer },
                { form: newCustomerCommission, store: currentAccount.commission.newCustomer },
                { form: restOfSiteCommission, store: currentAccount.commission.restOfSite }
            ];

            const hasCommissionChanged = commissions.some((commission) => commission.form !== commission.store);

            if (hasCommissionChanged) {
                await updateCommissions({
                    restOfSite: restOfSiteCommission ?? 0,
                    newCustomer: newCustomerCommission ?? 0,
                    coldCustomer: coldCustomerCommission ?? 0
                });
            }

            // Recalculate priority with final form values and update value in DB before submission
            await recalculatePriority({ saveResultInDb: true });

            await updateCampaign({ values: { boostId, title, commission: campaignCommission ?? 1, mode: 2 } });
            await getCampaignCommissions();
            const { ready, notConfirmed } = await submitCampaign(submitSelections);
            setFormSubmitting(false);

            if (ready || (notConfirmed.length === 1 && notConfirmed[0].name === 'hasValidScriptTags')) {
                localStorage.setItem('processing_campaign', campaign.id);
                navigate(ROUTE_PATHS.CAMPAIGNS);
            } else {
                navigate(`${ROUTE_PATHS.LAUNCH_CHECK}/${params?.id}`);
            }
        } catch (e) {
            setFormSubmitting(false);
        }
    };

    const creatives = campaign.creatives[0];

    return (
        <Layout hasSider style={{ justifyContent: 'space-between', gap: 40 }}>
            <Form form={form} onFinish={handleFinish}>
                <Step5Content />
            </Form>
            <Sider width='300px' style={{ backgroundColor: 'unset' }}>
                <Preview
                    font={creatives.fontName}
                    primaryColor={creatives.primaryColor}
                    secondaryColor={creatives.secondaryColor}
                    customSelectedProducts={accountProductsIds}
                    offerMessage={creatives.offerMessage}
                    imageFormat={creatives.productImageBehavior}
                    brandLink={store.accountsStore?.currentAccount?.domains?.[0]?.shopDomain}
                    brandName={store.accountsStore?.currentAccount?.companyName}
                    logo={campaign.creatives[0].logoUrl}
                />
            </Sider>
        </Layout>
    );
});

export default Step5;
